import {
  ConversationParticipantModel,
  MessageModel,
  MessageReplyType
} from '@models/conversations';
import * as _ from 'lodash';

/**
 * Creates a reply reference object for a message
 */
export const createReply = (isDirectReply: boolean, messageToReply: MessageModel) => {
  if (!messageToReply.id || !messageToReply.content) {
    throw new Error('Cannot create reply: message is missing required fields');
  }
  return {
    isDirectReply,
    id: messageToReply.id,
    content: messageToReply.content,
    createdAt: messageToReply.createdAt,
    conversationParticipant: messageToReply.conversationParticipant,
    attachments: messageToReply.attachments,
    createdByChatbot: messageToReply.createdByChatbot
  };
};

/**
 * Attaches reply information to messages in a sorted array
 * Returns a new array with reply information attached (immutable)
 */
export const attachReplyToMessages = (sortedMessages: MessageModel[]): MessageModel[] => {
  const messagesMap = new Map<number, { message: MessageModel; idx: number }>();
  const result: MessageModel[] = [];

  sortedMessages.forEach((message, currentMessageIdx) => {
    // Only add messages with real IDs to the map (optimistic messages can't be replied to)
    if (message.id !== null) {
      messagesMap.set(message.id, { message, idx: currentMessageIdx });
    }

    if (
      message?.inReplyToMessageId &&
      !message?.inReplyToMessage &&
      message?.replyType == MessageReplyType.EXPLICIT
    ) {
      const msgToReply = messagesMap.get(message?.inReplyToMessageId);
      if (msgToReply && msgToReply.message.id && msgToReply.message.content) {
        result.push({
          ...message,
          inReplyToMessage: createReply(
            currentMessageIdx === msgToReply.idx + 1,
            msgToReply.message
          )
        });
        return;
      }
    }

    result.push(message);
  });

  return result;
};

/**
 * Merges new messages with existing messages, handling optimistic updates
 * - Updates optimistic messages (acknowledged: false) with real data from API
 * - Adds new messages that don't exist in current list
 * - Deduplicates by both id and cid
 * - Returns sorted array with reply information attached
 */
export const mergeConversationMessages = (
  currentMessages: MessageModel[],
  newMessages: MessageModel[]
): MessageModel[] => {
  const merged: MessageModel[] = [];
  const processedIds = new Set<number | null>();
  const processedCids = new Set<string | null>();
  const processedOptimisticIndices = new Set<number>();

  // API returns messages already sorted by createdAt, so we can use them directly
  // First, handle acknowledged messages (update optimistic updates)
  newMessages.forEach((newMsg) => {
    // Try to find optimistic message by cid first
    let optimisticIdx = -1;
    if (newMsg.cid) {
      optimisticIdx = currentMessages.findIndex(
        (cm) => cm.cid === newMsg.cid && cm.acknowledged === false
      );
    }

    // If not found by cid, try to find by content and user (for cases where cid doesn't match)
    if (optimisticIdx === -1 && newMsg.id && newMsg.content) {
      optimisticIdx = currentMessages.findIndex(
        (cm) =>
          cm.acknowledged === false &&
          cm.id === null &&
          cm.content === newMsg.content &&
          cm.conversationParticipant?.user?.isMe === true &&
          newMsg.conversationParticipant?.user?.isMe === true
      );
    }

    // If still not found by cid or content, and newMsg has id but no content (backend doesn't return full data)
    // Find the LAST optimistic message (most recent) from the same user
    // NOTE: This fallback is fragile if multiple messages are sent rapidly
    // and API responses arrive out of order. Ideally, the API should always
    // return the cid for proper correlation. This fallback assumes responses
    // arrive in the same order as requests, which may not always be true.
    if (optimisticIdx === -1 && newMsg.id && !newMsg.content) {
      // Find all optimistic messages from the same user
      const optimisticMessages = currentMessages
        .map((cm, idx) => ({ message: cm, idx }))
        .filter(
          ({ message }) =>
            message.acknowledged === false &&
            message.id === null &&
            message.conversationParticipant?.user?.isMe ===
              newMsg.conversationParticipant?.user?.isMe
        );

      // Take the LAST one (most recent)
      // WARNING: This may incorrectly match if multiple optimistic messages
      // are pending and API responses arrive out of order
      if (optimisticMessages.length > 0) {
        optimisticIdx = optimisticMessages[optimisticMessages.length - 1].idx;
      }
    }

    if (optimisticIdx > -1) {
      const optimisticMsg = currentMessages[optimisticIdx];
      // Update optimistic message with real data, preserving content and cid from optimistic message if server didn't return them
      // IMPORTANT: Use createdAt from newMsg (API response) to ensure correct sorting order
      merged.push({
        ...optimisticMsg,
        ...newMsg,
        id: newMsg.id,
        acknowledged: true,
        content: newMsg.content || optimisticMsg.content,
        cid: newMsg.cid || optimisticMsg.cid || null,
        createdAt: newMsg.createdAt // Ensure we use createdAt from API response for correct sorting
      });
      if (newMsg.cid) processedCids.add(newMsg.cid);
      if (optimisticMsg.cid) processedCids.add(optimisticMsg.cid);
      if (newMsg.id) processedIds.add(newMsg.id);
      processedOptimisticIndices.add(optimisticIdx);
    }
  });

  // Then, add all current messages that weren't replaced
  currentMessages.forEach((currentMsg, idx) => {
    // Skip optimistic messages that were already matched and merged in step 1
    if (processedOptimisticIndices.has(idx)) {
      return; // Skip, already processed
    }

    if (currentMsg.cid && processedCids.has(currentMsg.cid)) {
      return; // Skip, already processed
    }
    if (currentMsg.id && processedIds.has(currentMsg.id)) {
      return; // Skip, already processed
    }

    // Skip optimistic messages if there's a matching new message waiting to be added
    if (currentMsg.acknowledged === false && currentMsg.id === null) {
      const matchingNewMsg = newMessages.find(
        (nm) =>
          !processedIds.has(nm.id || 0) &&
          ((nm.cid && nm.cid === currentMsg.cid) ||
            (nm.id &&
              nm.content === currentMsg.content &&
              nm.conversationParticipant?.user?.isMe === true &&
              currentMsg.conversationParticipant?.user?.isMe === true))
      );
      if (matchingNewMsg) {
        return; // Skip optimistic message, it will be replaced by new message in step 3
      }
    }

    merged.push(currentMsg);
    if (currentMsg.id) processedIds.add(currentMsg.id);
    if (currentMsg.cid) processedCids.add(currentMsg.cid);
  });

  // Finally, add new messages that weren't in current list
  newMessages.forEach((newMsg) => {
    // Skip if already processed in step 1 (replaced optimistic message)
    if (newMsg.id && processedIds.has(newMsg.id)) {
      return;
    }
    if (newMsg.cid && processedCids.has(newMsg.cid)) {
      return;
    }

    // Skip if there's already a message with the same id in merged array (prevent duplicates)
    if (newMsg.id) {
      const alreadyInMerged = merged.some((m) => m.id === newMsg.id);
      if (alreadyInMerged) {
        return;
      }
    }

    // Skip if there's already an optimistic message with the same cid in merged array
    if (newMsg.cid) {
      const hasOptimisticWithSameCid = merged.some(
        (m) => m.cid === newMsg.cid && m.acknowledged === false && m.id === null
      );
      if (hasOptimisticWithSameCid) {
        return;
      }
    }

    // Add new message only if it has an id (real message from server)
    if (newMsg.id) {
      merged.push(newMsg);
      if (newMsg.id) processedIds.add(newMsg.id);
      if (newMsg.cid) processedCids.add(newMsg.cid);
    }
  });

  // Sort by createdAt and attach replies
  const sorted = _.sortBy(merged, (m) => m.createdAt);
  return attachReplyToMessages(sorted);
};

/**
 * Creates an optimistic message for UI before API confirmation
 */
export const createOptimisticMessage = (
  content: string,
  meParticipant: ConversationParticipantModel,
  existingMessages: MessageModel[],
  cid: string,
  createdAt: string,
  inReplyToMessageId?: number
): MessageModel => {
  let inReplyTo = null;

  if (inReplyToMessageId && existingMessages) {
    const msgToReplyIdx = existingMessages.findIndex((m) => m.id === inReplyToMessageId);
    if (msgToReplyIdx > -1) {
      const m = existingMessages[msgToReplyIdx];
      if (m.id && m.content) {
        inReplyTo = createReply(existingMessages.length === msgToReplyIdx + 1, m);
      }
    }
  }

  return {
    id: null,
    cid,
    content,
    createdAt,
    attachments: null,
    conversationParticipant: meParticipant,
    acknowledged: false,
    createdByChatbot: false,
    recipientFeedbacks: [],
    inReplyToMessageId: inReplyToMessageId || null,
    inReplyToMessage: inReplyTo
  };
};
